import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth } from "@/App";
import { 
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Search,
  HelpCircle
} from "lucide-react";

const FAQ_CATEGORIES = [
  {
    id: "general",
    name: "General",
    questions: [
      {
        q: "What is Compass?",
        a: "Compass is a personal development tool designed to support reflection, clarity, and intentional action. It helps you build daily habits, recognize patterns in your behavior, and take meaningful steps toward personal transformation. It is not a substitute for therapy or professional advice."
      },
      {
        q: "Is this therapy?",
        a: "No. Compass is a self-guided personal development tool, not therapy or professional counseling. It's designed to support your growth journey through reflection and action. If you're experiencing mental health challenges, please seek support from a qualified mental health professional."
      },
      {
        q: "How should I use this?",
        a: "Use Compass daily: check your Signal, take the suggested action, and check in honestly about whether you followed through. The power is in consistency. Weekly, use the Reset feature to reflect on patterns and set intentions. Trust the process and be honest with yourself."
      },
      {
        q: "What if I feel overwhelmed?",
        a: "If you feel overwhelmed, pause. Use the Breathing Exercise feature to calm down. You can also take the Wellness Check to understand your current stress level. Remember: you can always take a break. If feelings persist, please reach out to a mental health professional."
      },
      {
        q: "Is Compass free to use?",
        a: "Compass offers core features for free, including the daily compass, basic reflections, and breathing exercises. Premium features and full access to Growth Studio content may require a subscription."
      },
      {
        q: "How is Compass different from other wellness apps?",
        a: "Compass focuses on behavioral transformation rather than just content consumption. We emphasize daily action, pattern recognition, and accountability to help you create lasting change."
      },
      {
        q: "What is the Daily Compass?",
        a: "The Daily Compass is your core daily practice. Each day you receive a signal (prompt), take an action, and check in with yourself. It helps build awareness and consistency over time."
      },
      {
        q: "What are the four transformation stages?",
        a: "The four stages are: Wake Up (identity & awareness), Burn the Map (deconstruction & reset), Whole (rebuild & design), and Purposeful Paths (execution systems). Each stage builds on the previous one."
      },
      {
        q: "What is Growth Studio?",
        a: "Growth Studio is our external content platform with in-depth courses, workshops, and resources. While Compass focuses on daily action and accountability, Growth Studio provides the deeper learning material."
      }
    ]
  },
  {
    id: "features",
    name: "Features",
    questions: [
      {
        q: "What is the 3-strike accountability system?",
        a: "When you check in 'No' three times in a row, Compass highlights this pattern to increase your awareness. It's not a punishment—it's designed to help you recognize recurring obstacles and address them."
      },
      {
        q: "How does pattern recognition work?",
        a: "Compass tracks your check-in responses and journal entries to identify recurring themes like fear, avoidance, distraction, or uncertainty. When patterns emerge, we suggest relevant tools to help you address them."
      },
      {
        q: "What is the Weekly Reset?",
        a: "The Weekly Reset is a reflection practice where you review your week, acknowledge what you followed through on, identify what you avoided, and set intentions for the coming week."
      },
      {
        q: "How do Reflections work?",
        a: "Reflections are quick check-ins that help you track your mood, sleep quality, gratitude, and thoughts. They're saved to your journal and help you build self-awareness over time."
      },
      {
        q: "What is the Vault?",
        a: "The Vault is your resource library containing worksheets, guides, and exercises organized into chambers. Each resource includes 'Use When' and 'Outcome' information to help you find what you need."
      },
      {
        q: "Can I track my progress?",
        a: "Yes! Your Profile shows your streak, completion stats, journal entries count, and overall journey progress through the four transformation stages."
      }
    ]
  },
  {
    id: "wellness-check",
    name: "Wellness Check",
    questions: [
      {
        q: "What is the Wellness Check?",
        a: "The Wellness Check is a brief assessment that helps you understand your current stress levels. It takes about 2-3 minutes and provides personalized recommendations based on your responses."
      },
      {
        q: "Is the Wellness Check a medical diagnosis?",
        a: "No, the Wellness Check is for informational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. If you're experiencing severe symptoms, please consult a healthcare provider."
      },
      {
        q: "How often should I take the Wellness Check?",
        a: "We recommend taking the Wellness Check whenever you feel the need to check in with yourself, or weekly to track your progress over time."
      },
      {
        q: "What do the stress levels mean?",
        a: "Minimal (0-4): Well-managed stress. Mild (5-9): Some stress, consider relaxation techniques. Moderate (10-14): Elevated stress, prioritize self-care. High (15+): Consider professional support."
      },
      {
        q: "Are my Wellness Check results saved?",
        a: "Currently, Wellness Check results are shown immediately but not saved to your profile. We recommend taking a screenshot or noting your score if you want to track changes over time."
      }
    ]
  },
  {
    id: "breathing",
    name: "Breathing Exercises",
    questions: [
      {
        q: "What breathing patterns are available?",
        a: "We offer three scientifically-backed breathing patterns: 4-7-8 Relaxing (best for sleep and deep relaxation), Box Breathing (great for focus and calm), and Simple Breathing (easy and calming for beginners)."
      },
      {
        q: "How long should I practice breathing exercises?",
        a: "Even 2-3 minutes of focused breathing can make a difference. For best results, try to practice for 5-10 minutes daily. The app will track your cycles so you can monitor your progress."
      },
      {
        q: "Can I use the breathing exercises without sound?",
        a: "Yes! You can toggle the sound on or off using the speaker icon in the breathing exercise screen. Visual cues will still guide you through each phase."
      },
      {
        q: "What is 4-7-8 breathing good for?",
        a: "The 4-7-8 pattern (4 seconds inhale, 7 seconds hold, 8 seconds exhale) activates your parasympathetic nervous system, making it excellent for reducing anxiety, falling asleep, and managing stress responses."
      },
      {
        q: "What is Box Breathing?",
        a: "Box Breathing uses equal intervals (4 seconds each) for inhale, hold, exhale, and hold. It's used by Navy SEALs and first responders for focus under pressure and is great for improving concentration."
      },
      {
        q: "When should I use breathing exercises?",
        a: "Use them anytime you feel stressed, anxious, or overwhelmed. They're also great as a morning routine, before important meetings, during breaks, or as part of your bedtime ritual."
      }
    ]
  },
  {
    id: "journal",
    name: "Journal & Privacy",
    questions: [
      {
        q: "Is my journal private?",
        a: "Yes, your journal is completely private. Only you can see your entries. We use encryption to protect your data, and we never share or sell your personal information."
      },
      {
        q: "Can I delete journal entries?",
        a: "Yes, you can delete any journal entry at any time. Go to your Journal, find the entry you want to remove, and use the delete option."
      },
      {
        q: "How is my data protected?",
        a: "We take your privacy seriously. All your personal data and journal entries are encrypted and stored securely. We never sell your data to third parties."
      },
      {
        q: "Can I delete my account and data?",
        a: "Yes, you can request account deletion at any time through your profile settings or by contacting our support team. All your data will be permanently removed within 30 days."
      },
      {
        q: "Are my Wellness Check results shared with anyone?",
        a: "No, your Wellness Check results are completely private and are not shared with anyone, including employers or insurance providers."
      },
      {
        q: "Can I export my data?",
        a: "We're working on a data export feature. In the meantime, you can contact support to request a copy of your data."
      }
    ]
  },
  {
    id: "account",
    name: "Account",
    questions: [
      {
        q: "How do I reset my password?",
        a: "Click 'Forgot Password' on the login page, enter your email, and we'll send you a reset link. Follow the instructions in the email to create a new password."
      },
      {
        q: "Can I change my email address?",
        a: "Currently, email changes require contacting support. We're working on adding this feature to your profile settings."
      },
      {
        q: "How do I update my profile?",
        a: "Go to your Profile page and click the settings icon. You can update your name and other profile information from there."
      },
      {
        q: "Can I use Compass on multiple devices?",
        a: "Yes! Your account syncs across devices. Simply log in with the same email and password on any device to access your data."
      },
      {
        q: "What happens to my streak if I miss a day?",
        a: "Your streak resets to zero if you miss a check-in. But don't worry—missing a day is part of the journey. The Weekly Reset helps you reflect on patterns and recommit."
      }
    ]
  },
  {
    id: "business",
    name: "For Business",
    questions: [
      {
        q: "Does Compass offer enterprise solutions?",
        a: "Yes! Compass for Business offers wellness solutions for health plans, employers, and consultants. Contact us for a demo to learn more about our enterprise offerings."
      },
      {
        q: "Can we customize Compass for our organization?",
        a: "Absolutely. We offer white-label solutions and custom branding options for enterprise clients. Our team will work with you to create a tailored wellness program."
      },
      {
        q: "What kind of reporting is available for organizations?",
        a: "We provide aggregate, anonymized engagement reports that help you understand how your team is using Compass, without compromising individual privacy."
      },
      {
        q: "How does pricing work for businesses?",
        a: "Pricing depends on your organization size and needs. We offer flexible plans for small teams to large enterprises. Contact us for a custom quote."
      },
      {
        q: "Can employees use Compass anonymously?",
        a: "Yes. In enterprise deployments, individual user data is never shared with employers. Only aggregate, anonymized statistics are included in reports."
      },
      {
        q: "How do we get started with Compass for Business?",
        a: "Visit our For Business page and click 'Request a Demo'. Our team will reach out to understand your needs and show you how Compass can help your organization."
      }
    ]
  }
];

export default function FAQPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [activeCategory, setActiveCategory] = useState("general");

  const toggleQuestion = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setExpandedQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredCategories = FAQ_CATEGORIES.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const displayCategories = searchQuery ? filteredCategories : FAQ_CATEGORIES;

  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <button 
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-[#d4e4d4] flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5 text-[#0B2821]" />
            </button>
            <div>
              <h1 className="text-2xl font-heading text-[#0B2821]">FAQ</h1>
              <p className="text-[#0B2821]/50 text-sm">Frequently asked questions</p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0B2821]/40" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/60 border border-[#0B2821]/10 text-[#0B2821] placeholder:text-[#0B2821]/40 focus:outline-none focus:border-primary"
              data-testid="faq-search"
            />
          </div>
        </div>
      </div>

      {/* Category Pills - Full width scrollable, outside max-w container */}
      {!searchQuery && (
        <div className="overflow-x-auto pb-4 mb-2 scrollbar-hide px-4">
          <div className="flex gap-2 max-w-3xl mx-auto">
            {FAQ_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all shrink-0 ${
                  activeCategory === category.id
                    ? "bg-[#7a9e7a] text-white"
                    : "bg-white/80 text-[#0B2821] hover:bg-white"
                }`}
                data-testid={`category-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="px-4 py-2 max-w-3xl mx-auto">

        {/* Questions */}
        {displayCategories.map((category) => (
          (searchQuery || category.id === activeCategory) && (
            <div key={category.id} className="mb-8">
              {searchQuery && (
                <h2 className="text-lg font-heading text-[#0B2821] mb-4">{category.name}</h2>
              )}
              
              <div className="space-y-3">
                {category.questions.map((item, index) => {
                  const key = `${category.id}-${index}`;
                  const isExpanded = expandedQuestions[key];
                  
                  return (
                    <Card 
                      key={key} 
                      className="glass-card overflow-hidden cursor-pointer"
                      onClick={() => toggleQuestion(category.id, index)}
                      data-testid={`faq-${category.id}-${index}`}
                    >
                      <CardContent className="p-0">
                        <div className="p-4 flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#d0e0e8] flex items-center justify-center shrink-0 mt-0.5">
                              <HelpCircle className="w-4 h-4 text-[#5a7090]" />
                            </div>
                            <p className="text-[#0B2821] font-medium">{item.q}</p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-[#0B2821]/40 shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#0B2821]/40 shrink-0" />
                          )}
                        </div>
                        
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-0 ml-11">
                            <p className="text-[#0B2821]/70 text-sm leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )
        ))}

        {/* No results */}
        {searchQuery && displayCategories.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-[#0B2821]/20 mx-auto mb-4" />
            <p className="text-[#0B2821]/60">No questions found matching "{searchQuery}"</p>
            <Button 
              variant="ghost"
              className="mt-4 text-primary"
              onClick={() => setSearchQuery("")}
            >
              Clear search
            </Button>
          </div>
        )}

        {/* Contact Support */}
        <Card className="glass-card mt-8">
          <CardContent className="p-6 text-center">
            <h3 className="text-[#0B2821] font-heading text-lg mb-2">Still have questions?</h3>
            <p className="text-[#0B2821]/60 text-sm mb-4">
              Can't find what you're looking for? Reach out to our support team.
            </p>
            <Button 
              className="btn-primary"
              onClick={() => window.location.href = "mailto:info@truenorth-zone.com"}
            >
              Contact Support
            </Button>
          </CardContent>
        </Card>
      </main>

      {user && <BottomNavigation />}
    </div>
  );
}
